import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, RefreshCw, Home, Copy } from "lucide-react";
import * as Sentry from "@sentry/react";
import { trackEvent, getRecentActions, getPostHogSessionUrl } from "@/lib/posthog";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
  copied: boolean;
}

/** Generate a short readable error ID for support reference */
function generateErrorId(): string {
  return `ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
}

/**
 * Enhanced Error Boundary with full production error logging.
 * Errors are reported to:
 * - Console (always, for DevTools debugging)
 * - Sentry (crash report with component stack + error ID tag)
 * - PostHog (ui_crash event for analytics)
 */
export class EnhancedErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      copied: false,
    };
  }

  /** ກວດສອບວ່າ error ນີ້ແມ່ນ chunk load error (ເກີດຈາກ deploy ໃໝ່) */
  static isChunkLoadError(error: Error): boolean {
    const msg = (error.message || "").toLowerCase();
    return (
      msg.includes("failed to fetch dynamically imported module") ||
      msg.includes("error loading dynamically imported module") ||
      msg.includes("importing a module script failed") ||
      msg.includes("unable to preload css") ||
      error.name.toLowerCase() === "chunkloaderror"
    );
  }

  static getChunkReloadKey(): string {
    const build = String(import.meta.env.VITE_BUILD_COMMIT || "unknown").replace(/[^a-z0-9_-]/gi, "");
    const route = typeof window === "undefined" ? "unknown" : window.location.pathname.replace(/[^a-z0-9_-]/gi, "_");
    return `crs_chunk_reload_${build}_${route}`;
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // ຖ້າເປັນ chunk load error → auto reload ທັນທີ (ບໍ່ show error page)
    if (EnhancedErrorBoundary.isChunkLoadError(error)) {
      // ຈຳກັດ reload ຕາມ build ແລະ route ເພື່ອປ້ອງກັນ reload loop ແລະບໍ່ໃຫ້ route ອື່ນໃຊ້ retry ຮ່ວມກັນ
      const reloadKey = EnhancedErrorBoundary.getChunkReloadKey();
      const reloadCount = parseInt(sessionStorage.getItem(reloadKey) || "0");
      if (reloadCount < 2) {
        sessionStorage.setItem(reloadKey, String(reloadCount + 1));
        trackEvent("chunk_load_recovery", {
          page: window.location.pathname,
          attempt: reloadCount + 1,
          build_commit: import.meta.env.VITE_BUILD_COMMIT || "unknown",
        });
        window.location.reload();
        return { hasError: false }; // ຈະ reload ກ່ອນ render
      }
      // ຖ້າ reload ແລ້ວ 2 ຄັ້ງຍັງ error → show error page ຕາມປົກກະຕິ
      sessionStorage.removeItem(reloadKey);
    }
    return { hasError: true, error, errorId: generateErrorId() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = this.state.errorId ?? generateErrorId();

    // ✅ Always log to console — visible in browser DevTools in all environments
    console.group(`🔴 [EnhancedErrorBoundary] ${errorId}`);
    console.error("Error:", error.message);
    console.error("Stack:", error.stack);
    console.error("Component Stack:", errorInfo.componentStack);
    console.error("Full Error Object:", error);
    console.groupEnd();

    // ─── Sentry: capture crash with full context ───────────────────────────
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.withScope((scope) => {
        scope.setTag("error_id", errorId);
        scope.setTag("page", window.location.pathname);
        scope.setExtra("componentStack", errorInfo.componentStack);
        scope.setExtra("errorId", errorId);
        scope.setExtra("is_mobile", window.innerWidth < 768);
        scope.setLevel("fatal");
        Sentry.captureException(error);
      });
    }

    // ─── PostHog: track ui_crash event ────────────────────────────────────
    if (import.meta.env.VITE_POSTHOG_KEY) {
      trackEvent("ui_crash", {
        error_id: errorId,
        error_message: error.message?.substring(0, 200),
        page: window.location.pathname,
        is_mobile: window.innerWidth < 768,
        device_width: window.innerWidth,
      });
    }

    // ─── Telegram: send instant crash alert via server endpoint ──────────
    // ດຶງ username ຈາກ localStorage (ຖ້າມີ) ເພື່ອລະບຸ user ທີ່ crash
    const storedUser = (() => {
      try { return JSON.parse(localStorage.getItem("crs_user") || "{}"); } catch { return {}; }
    })();
    // ດຶງ breadcrumbs (ສິ່ງທີ່ user ເຮັດກ່ອນ crash) ແລະ PostHog session URL
    const recentActionsText = getRecentActions(10);
    const sessionReplayUrl = getPostHogSessionUrl();
    fetch("/api/client-crash", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        errorId,
        errorMessage: error.message?.substring(0, 300),
        componentStack: errorInfo.componentStack?.substring(0, 500),
        page: window.location.pathname,
        isMobile: window.innerWidth < 768,
        deviceWidth: window.innerWidth,
        username: storedUser?.username || storedUser?.name || undefined,
        userRole: storedUser?.role || undefined,
        userBranchName: storedUser?.branchName || undefined,
        userUnitName: storedUser?.unitName || undefined,
        userPosition: storedUser?.position || undefined,
        recentActions: recentActionsText,
        sessionReplayUrl: sessionReplayUrl || undefined,
      }),
    }).catch(() => {}); // fire-and-forget, never block UI

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    this.setState({ error, errorInfo, errorId });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      copied: false,
    });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleCopyError = () => {
    const { error, errorInfo, errorId } = this.state;
    const text = [
      `Error ID: ${errorId}`,
      `Message: ${error?.message}`,
      `Stack: ${error?.stack}`,
      `Component Stack: ${errorInfo?.componentStack}`,
    ].join("\n\n");

    // navigator.clipboard requires HTTPS + user gesture
    // On some mobile browsers it throws "The operation is insecure" (SecurityError)
    // Use fallback: document.execCommand (deprecated but works everywhere)
    const tryClipboard = async () => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for non-HTTPS or mobile browsers that block clipboard API
          const el = document.createElement("textarea");
          el.value = text;
          el.style.position = "fixed";
          el.style.opacity = "0";
          document.body.appendChild(el);
          el.focus();
          el.select();
          document.execCommand("copy");
          document.body.removeChild(el);
        }
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 2000);
      } catch {
        // Silent fail — ຖ້າ copy ບໍ່ໄດ້ ກໍ່ບໍ່ crash
      }
    };
    tryClipboard();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, errorId, copied } = this.state;

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-destructive/10">
                  <AlertTriangle className="size-6 text-destructive" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-2xl">
                    ເກີດຂໍ້ຜິດພາດບາງຢ່າງ
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    ຂໍອະໄພ, ມີບາງຢ່າງຜິດພາດ. ກະລຸນາລອງໃໝ່ອີກຄັ້ງ.
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Error ID — always visible for support reference */}
              {errorId && (
                <div className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg">
                  <span className="text-xs text-muted-foreground flex-1">
                    Error ID: <span className="font-mono font-semibold text-foreground">{errorId}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={this.handleCopyError}
                  >
                    <Copy className="size-3 mr-1" />
                    {copied ? "ຄັດລອກແລ້ວ" : "ຄັດລອກ"}
                  </Button>
                </div>
              )}

              {/* Error message — always visible */}
              {error && (
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-3">
                  <p className="text-xs font-semibold text-destructive uppercase tracking-wide mb-1">
                    ຂໍ້ຜິດພາດ
                  </p>
                  <p className="text-sm font-mono text-destructive break-all">
                    {error.message}
                  </p>
                </div>
              )}

              {/* Full stack trace — shown in development only */}
              {import.meta.env.DEV && error && (
                <details className="bg-muted p-4 rounded-lg">
                  <summary className="cursor-pointer font-medium text-sm">
                    Stack Trace (Development Mode)
                  </summary>
                  <div className="mt-3 space-y-2">
                    {error.stack && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Stack:</p>
                        <pre className="text-xs font-mono bg-background p-2 rounded mt-1 overflow-x-auto whitespace-pre-wrap">
                          {error.stack}
                        </pre>
                      </div>
                    )}
                    {errorInfo?.componentStack && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Component Stack:</p>
                        <pre className="text-xs font-mono bg-background p-2 rounded mt-1 overflow-x-auto whitespace-pre-wrap">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    // ສຳລັບ chunk error: hard reload ດີກວ່າ soft reset
                    if (error && EnhancedErrorBoundary.isChunkLoadError(error)) {
                      sessionStorage.removeItem("crs_chunk_reload");
                      window.location.reload();
                    } else {
                      this.handleReset();
                    }
                  }}
                  className="flex-1"
                  variant="default"
                >
                  <RefreshCw className="size-4 mr-2" />
                  ລອງໃໝ່ອີກຄັ້ງ
                </Button>
                <Button
                  onClick={this.handleGoHome}
                  className="flex-1"
                  variant="outline"
                >
                  <Home className="size-4 mr-2" />
                  ກັບໄປໜ້າຫຼັກ
                </Button>
              </div>

              {/* Help text */}
              <p className="text-xs text-muted-foreground text-center">
                ຖ້າບັນຫາຍັງຄົງມີຢູ່, ກະລຸນາຕິດຕໍ່ຜູ້ດູແລລະບົບ ພ້ອມ Error ID ຂ້າງເທິງ
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC version for functional components
 * Usage: wrap components that might throw errors
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <EnhancedErrorBoundary fallback={fallback}>
        <Component {...props} />
      </EnhancedErrorBoundary>
    );
  };
}
